// codeViolationEvidence — the "can I verify this lead?" facts for a
// code-enforcement / condemnation lead (Sprint 1 item 4).
//
// The backend has always stored a case count, the order date and a link back to
// the record; until the API exposed them (models.LeadSignal evidenceUrl /
// openCases / orderDate / stage) a user had to take the "Code Violation" pill on
// faith. This module turns those fields into display strings in ONE place so the
// property modal and the lead card cannot drift apart.
//
// Deliberately NOT here: owner or mailing data. Nothing below reads
// property.owner, and nothing may be added that does.

import { LeadSignal, Property } from "../types";
import { formatShortDate } from "./format";
import { leadTypeMeta } from "./leadTypeMeta";
import { resolvePropertyBadge } from "./dataQuality";

/** Plain-language rollup stage, keyed by backend codeviolation.Stage.String(). */
const STAGE_WORDS: Record<string, string> = {
  notice: "Notice of violation sent to the owner",
  hearing: "Administrative hearing called or advertised",
  repair_order: "Repair order issued (Findings of Fact)",
  demolition_order: "Demolition order issued (Findings of Fact)",
};

/**
 * Same wording as the backend risk flag (riskflags/evaluators.go codeViolation)
 * so the two never contradict. The full statute-backed caveat sits in the risk
 * flag list directly below this panel.
 */
export const CODE_VIOLATION_CAVEAT =
  "Verify the current case status with the enforcing department before bidding.";

const CREDENTIAL_PARAMS = new Set([
  "sig",
  "signature",
  "x-amz-signature",
  "x-goog-signature",
  "skoid",
  "token",
  "access_token",
]);

/**
 * Returns `raw` only when it is an absolute http(s) URL with a host. The
 * backend already filters (models.SafeEvidenceURL); this is the independent
 * second check at the point the value becomes an href, so a bad value that
 * somehow arrives (cached payload, fixture, future endpoint) cannot become
 * `javascript:` on click.
 */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  // Control characters can smuggle a scheme past a prefix check ("java\tscript:").
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return null;
  }
  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (!u.hostname || u.username || u.password) return null;
    // Never link a credential-bearing URL (signed blob/S3 links); mirrors the
    // backend's models.SafeEvidenceURL.
    for (const k of Array.from(u.searchParams.keys())) {
      if (CREDENTIAL_PARAMS.has(k.toLowerCase())) return null;
    }
    return u.toString();
  } catch {
    return null;
  }
}

export interface CodeViolationEvidence {
  signalType: "code_enforcement" | "condemnation";
  /** Headline from resolvePropertyBadge, e.g. "Repair order", "Condemned". */
  headline: string;
  openCases: number | null;
  /** "Open code-enforcement case(s): 2" — null when the count is unknown. */
  openCasesText: string | null;
  /** ISO date, null when the source reports none. */
  orderDate: string | null;
  /** "Mar 4, 2026" — null when no date. */
  orderDateText: string | null;
  /** Plain-words rollup stage, null when the source does not compute one. */
  stageText: string | null;
  /** http(s) link to the record or its portal, null when absent/unsafe. */
  evidenceUrl: string | null;
  /** Free-text sub-status the source wrote, e.g. "2 open cases since 2019". */
  subStatus: string | null;
  /** One-line summary for the lead card, "" when there is nothing to add. */
  compact: string;
}

/** The code-violation signal to explain: condemnation outranks code_enforcement. */
export function codeViolationSignal(signals: LeadSignal[] | undefined | null): LeadSignal | null {
  if (!signals) return null;
  return (
    signals.find((s) => s.signalType === "condemnation") ??
    signals.find((s) => s.signalType === "code_enforcement") ??
    null
  );
}

/** "Open code-enforcement case(s): N" — the label the playbook asserts on. */
export function openCasesText(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n) || n < 0) return null;
  return `Open code-enforcement case(s): ${n}`;
}

export function codeViolationEvidence(property: Property): CodeViolationEvidence | null {
  const s = codeViolationSignal(property.leadSignals);
  if (!s) return null;

  const badge = resolvePropertyBadge(property, s.signalType);
  const headline = badge.isLeadType ? badge.label : leadTypeMeta(s.signalType).label;

  const openCases = typeof s.openCases === "number" ? s.openCases : null;
  const orderDate = s.orderDate ?? null;
  const orderDateText = orderDate ? formatShortDate(orderDate) : null;
  const stageText = s.stage ? STAGE_WORDS[s.stage] ?? null : null;

  const compactParts: string[] = [];
  if (openCases != null) compactParts.push(`${openCases} open case${openCases === 1 ? "" : "s"}`);
  if (orderDateText) compactParts.push(`order ${orderDateText}`);

  return {
    signalType: s.signalType as "code_enforcement" | "condemnation",
    headline,
    openCases,
    openCasesText: openCasesText(openCases),
    orderDate,
    orderDateText,
    stageText,
    evidenceUrl: safeHttpUrl(s.evidenceUrl),
    subStatus: s.subStatus?.trim() || null,
    compact: compactParts.join(" · "),
  };
}
